import { API_ENDPOINTS } from '../constants/api';
import { apiFetch } from '../utils/apiClient';

export const ACTIVE_JOB_STATUSES = new Set(['queued', 'running', 'cancelling']);
export const TERMINAL_JOB_STATUSES = new Set(['completed', 'partial_completed', 'failed', 'cancelled']);
export const JOB_REGISTER_EVENT = 'testsuite:job-register';

export const announceJob = (job, adapter = job?.adapter || 'generic') => {
  const normalized = normalizeJob(job, adapter);
  if (normalized.id && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(JOB_REGISTER_EVENT, { detail: normalized }));
  }
  return normalized;
};

const parseError = async (response, fallback) => {
  const payload = await response.json().catch(() => ({}));
  return new Error(payload?.detail || payload?.message || fallback);
};

export const normalizeJob = (job = {}, adapter = 'generic') => {
  const progress = job.progress || {};
  const total = Number(progress.total ?? job.total_items ?? job.item_count ?? 0) || 0;
  const current = Number(progress.current ?? job.completed_items ?? job.completed_count ?? 0) || 0;
  const percent = Number(progress.percent ?? (total ? (current / total) * 100 : 0));
  return {
    ...job,
    id: String(job.id || job.job_id || ''),
    adapter,
    status: String(job.status || 'queued').toLowerCase(),
    application_id: job.application_id || null,
    source_id: job.source_id || job.pdf_id || null,
    type: job.type || job.mode || (adapter === 'bulk' ? 'test_case_generation' : 'background_job'),
    progress: {
      stage: progress.stage || job.stage || (job.status === 'queued' ? 'waiting' : job.status),
      message: progress.message || job.message || null,
      current,
      total,
      percent: Math.max(0, Math.min(100, Math.round(Number.isFinite(percent) ? percent : 0))),
    },
  };
};

const fetchJson = async (url, options, fallback) => {
  const response = await apiFetch(url, options);
  if (!response.ok) throw await parseError(response, fallback);
  return response.json();
};

export const listJobs = async (filters = {}, options = {}) => {
  const query = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') query.set(key, value);
  });
  const payload = await fetchJson(
    `${API_ENDPOINTS.JOBS}${query.toString() ? `?${query}` : ''}`,
    { signal: options.signal },
    'Unable to load background jobs.'
  );
  return (payload.jobs || []).map((job) => normalizeJob(job));
};

export const loadJobSnapshot = async (reference, options = {}) => {
  const id = String(reference?.id || reference || '');
  const adapter = reference?.adapter || 'generic';
  if (!id) throw new Error('A job id is required.');
  if (adapter === 'bulk') {
    const [jobPayload, itemsPayload] = await Promise.all([
      fetchJson(API_ENDPOINTS.BULK_GENERATION_BY_ID(id), { signal: options.signal }, 'Unable to load generation status.'),
      fetchJson(API_ENDPOINTS.BULK_GENERATION_ITEMS(id), { signal: options.signal }, 'Unable to load generation items.'),
    ]);
    return { job: normalizeJob(jobPayload.bulk_job || {}, 'bulk'), items: itemsPayload.items || [] };
  }
  const [jobPayload, itemsPayload] = await Promise.all([
    fetchJson(API_ENDPOINTS.JOB_BY_ID(id), { signal: options.signal }, 'Unable to load job status.'),
    fetchJson(API_ENDPOINTS.JOB_ITEMS(id), { signal: options.signal }, 'Unable to load job items.'),
  ]);
  return { job: normalizeJob(jobPayload.job || {}), items: itemsPayload.items || [] };
};

export const cancelJob = async (job) => {
  const endpoint = job.adapter === 'bulk'
    ? API_ENDPOINTS.BULK_GENERATION_CANCEL(job.id)
    : API_ENDPOINTS.JOB_CANCEL(job.id);
  const payload = await fetchJson(endpoint, { method: 'POST' }, 'Unable to cancel this job.');
  return normalizeJob(payload.job || payload.bulk_job || job, job.adapter);
};

export const retryJob = async (job) => {
  if (job.adapter === 'bulk') throw new Error('Retry this legacy job from its workflow screen.');
  const payload = await fetchJson(API_ENDPOINTS.JOB_RETRY(job.id), { method: 'POST' }, 'Unable to retry this job.');
  return normalizeJob(payload.job || job);
};

export const loadJobResult = async (job, options = {}) => fetchJson(
  API_ENDPOINTS.JOB_RESULT(String(job?.id || job || '')),
  { signal: options.signal },
  'The generated result is not available.'
);

export const submitJobOperation = async (endpoint, request = {}, options = {}) => {
  const payload = await fetchJson(endpoint, request, options.errorMessage || 'Unable to start this job.');
  const rawJob = payload.job || payload.bulk_job;
  if (!rawJob?.id) throw new Error('The operation did not return a job.');
  return { payload, job: announceJob(rawJob, payload.bulk_job ? 'bulk' : 'generic') };
};

const waitWithAbort = (delay, signal) => new Promise((resolve, reject) => {
  const jitter = Math.round(delay * (0.85 + Math.random() * 0.3));
  const timer = setTimeout(resolve, jitter);
  signal?.addEventListener('abort', () => {
    clearTimeout(timer);
    reject(new DOMException('The operation was aborted.', 'AbortError'));
  }, { once: true });
});

export const waitForJobResult = async (reference, options = {}) => {
  const startedAt = Date.now();
  let delay = options.initialDelayMs || 1200;
  while (!options.signal?.aborted) {
    const snapshot = await loadJobSnapshot(reference, options);
    const { job } = snapshot;
    announceJob(job, job.adapter);
    options.onUpdate?.(snapshot);
    if (TERMINAL_JOB_STATUSES.has(job.status)) {
      if (job.status === 'completed' || job.status === 'partial_completed') return snapshot;
      // A test failure still produces a report. Let execution screens load it,
      // while worker failures without results keep their normal error handling.
      if (options.acceptFailedExecution && job.status === 'failed'
        && ['playwright_execution', 'feature_execution', 'appium_execution'].includes(job.type)
        && job.artifact_refs?.some(ref => ['playwright_run_result', 'feature_run_result', 'appium_run_result'].includes(ref?.kind))) {
        return snapshot;
      }
      throw new Error(job.error?.message || job.error || 'The job did not complete successfully.');
    }
    if (Date.now() - startedAt > (options.timeoutMs || 45 * 60 * 1000)) {
      throw new Error('The job is still running. You can follow it from the job center.');
    }
    await waitWithAbort(delay, options.signal);
    delay = Math.min(job.status === 'queued' ? delay * 1.5 : delay * 1.2, 10000);
  }
  throw new DOMException('The operation was aborted.', 'AbortError');
};
